/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 * Fast Promised Payment Buttons component
 *
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.36.0
 */

import { FC, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';


import { Button, LinkBlock } from '@/main/components/common';
import { TableFooterFastActionsButtonsStyled } from '@/main/components/common/TableFooter';
import ArrowTooltip from '@/main/components/common/Tooltip/Tooltip';
import { makeSelectPageAccountInfoTranslation, makeSelectTranslationPagePersonalAccount } from '@/main/modules/internationalization/intSelectors';
import { modalOpen } from '@/main/modules/modal/modalActions';
import { IModalRenderProps } from '@/main/modules/modal/types/modalTypes';
import PromisedPaymentModal from '@/main/modules/promisedPayment/components/PromisedPaymentModal';
import { RoutePath } from '@/main/routes/constants/routesConstants';

interface IFastPromisedPaymentButtonsProps {
    clientId: string;
    isDisabled: boolean;
    tooltipText?: string;
}



export const FastPromisedPaymentButtons: FC<IFastPromisedPaymentButtonsProps> = (
    { clientId, isDisabled, tooltipText },
) => {

    const dispatch = useDispatch();

    const { tableFooter } = useSelector(makeSelectTranslationPagePersonalAccount());
    const { financialInfo } = useSelector(makeSelectPageAccountInfoTranslation());

    const promisedPaymentButtonHandler = useCallback(() => {
        dispatch(
            modalOpen({
                render: (modalProps: IModalRenderProps) => (
                    <PromisedPaymentModal clientId={clientId} modalId={modalProps.id} />
                ),
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    return (
        <TableFooterFastActionsButtonsStyled>
            <ArrowTooltip
                // Tooltip is displayed when the button is locked
                disableHoverListener={!isDisabled}
                key={tooltipText}
                placement="top"
                title={tooltipText}
                titleAlign="center"
            >
                <div>
                    <Button
                        className="widget-account__btn-promised-payment"
                        dataName="promisedPaymentButton"
                        disabled={isDisabled}
                        kind="mediumNormal"
                        onClick={promisedPaymentButtonHandler}
                    >
                        {financialInfo.promisedPayments.buttonLabel}
                    </Button>        
                </div>
            </ArrowTooltip>
            <LinkBlock dataName="redirectToDashboard" url={RoutePath.dashboard}>
                {tableFooter.cancel}
            </LinkBlock>   
        </TableFooterFastActionsButtonsStyled>
    )};
